"use client";

import { cn } from "@/lib/utils";

export type BadgePosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";
export type BadgeGap = "sm" | "md";

export interface BadgeStackProps {
  /** Position of the badge stack within its container */
  position: BadgePosition;
  /** Gap between badges */
  gap?: BadgeGap;
  /** Additional CSS classes */
  className?: string;
  /** Badge elements */
  children: React.ReactNode;
}

const positionClasses: Record<BadgePosition, string> = {
  "top-left": "top-4 left-4",
  "top-right": "top-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "bottom-right": "bottom-4 right-4",
};

const gapClasses: Record<BadgeGap, string> = {
  sm: "gap-1",
  md: "gap-2",
};

/**
 * BadgeStack - Absolutely positioned container for vertical badge arrangements
 *
 * Use this component to position badges in corners of cards or hero sections.
 * Children should typically be Badge components.
 *
 * @example
 * <div className="relative">
 *   <BadgeStack position="top-left">
 *     <Badge>Premium</Badge>
 *     <Badge>Active</Badge>
 *   </BadgeStack>
 * </div>
 */
export function BadgeStack({
  position,
  gap = "md",
  className,
  children,
}: BadgeStackProps) {
  return (
    <div
      className={cn(
        "absolute z-10 flex flex-col",
        positionClasses[position],
        gapClasses[gap],
        className
      )}
    >
      {children}
    </div>
  );
}

export interface BadgeRowProps {
  /** Gap between badges */
  gap?: BadgeGap;
  /** Wrap badges to next line if needed */
  wrap?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Badge elements */
  children: React.ReactNode;
}

/**
 * BadgeRow - Horizontal badge arrangement
 *
 * Use this component for inline badge displays.
 *
 * @example
 * <BadgeRow gap="sm">
 *   <Badge>Golf</Badge>
 *   <Badge>Tennis</Badge>
 * </BadgeRow>
 */
export function BadgeRow({
  gap = "md",
  wrap = false,
  className,
  children,
}: BadgeRowProps) {
  return (
    <div
      className={cn(
        "flex items-center",
        gapClasses[gap],
        wrap && "flex-wrap",
        className
      )}
    >
      {children}
    </div>
  );
}

export default BadgeStack;
